import type { OperationVariables, TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import { Fragment, useState, type CSSProperties } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { ManagerProgressionTaskState } from '@sorare/core/src/__generated__/globalTypes';
import { Dot } from '@sorare/core/src/atoms/icons/Dot.tsx';
import { Portal } from '@sorare/core/src/atoms/layout/Portal/index.tsx';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LinkOther } from '@sorare/core/src/atoms/navigation/Box';
import { BrandS, BrandXXS } from '@sorare/core/src/atoms/typography';
import { StepperDialog } from '@sorare/core/src/components/StepperDialog/index.tsx';
import { SeriesBoardTimer } from '@sorare/core/src/components/commonGame/SeriesBoardTimer/index.tsx';
import {
  ANY_SPORT_SERIES_TASK_COMPOSE_TEAM,
  ANY_SPORT_SERIES_TASK_LINEUPS,
} from '@sorare/core/src/constants/__generated__/routes.ts';
import { useSessionReplayShutdown } from '@sorare/core/src/contexts/events/useSessionReplayShutdown.ts';
import { useSportContext } from '@sorare/core/src/contexts/sport/index.ts';
import { idFromObject } from '@sorare/core/src/gql/idFromObject.ts';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import type { RefetchFunction } from '@sorare/core/src/hooks/graphql/useQuery';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags.tsx';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';
import { LIFECYCLE, useLifecycle } from '@sorare/core/src/hooks/useLifecycle';
import { useShowProOnboardingBadge } from '@sorare/core/src/hooks/useShowProOnboardingBadge.ts';
import { isTypeFn } from '@sorare/core/src/lib/gql';
import {
  getBoardMobileDecoration,
  useCommonGameSet,
} from '@sorare/core/src/set';
import { useCardSetBranding } from '@sorare/core/src/set/branding/useCardSetBranding.ts';
import {
  desktopAndAbove,
  laptopAndAbove,
} from '@sorare/core/src/style/mediaQuery';
import type { HexColor } from '@sorare/core/src/style/types';
import { css } from '@sorare/css';

import { ArcadeModeIcon } from '#src/components/ArcadeModeIcon/index.tsx';
import { FullHeightBoard } from '#src/components/boards/FullHeightBoard/index.tsx';
import { HotStreakTile } from '#src/components/boards/HotStreakTile/index.tsx';
import { Lifes, LifesContainer } from '#src/components/boards/Lifes/index.tsx';
import { AnyTaskBasicClaimStory } from '#src/components/missions/AnyTaskBasicClaimStory/index.tsx';
import { HotStreaksTutorialStory } from '#src/components/series/HotStreaksTutorialStory/index.tsx';
import { RefereeExclusiveReward } from '#src/components/series/inviteFriends/RefereeExclusiveReward/index.tsx';
import { ReferrerReward } from '#src/components/series/inviteFriends/ReferrerReward/index.tsx';
import { GuessTheScoreBadge } from '#src/components/squad/GuessTheScoreBadge/index.tsx';
import { ProOnboardingBadge } from '#src/components/squad/ProOnboardingBadge/index.tsx';
import { SquadEntryBadge } from '#src/components/squad/SquadEntryBadge/index.tsx';
import { useIsSquadEnabled } from '#src/hooks/squad/useIsSquadEnabled.ts';
import { seriesModeMessages } from '#src/lib/gameMode.ts';

import { SeriesMissionsWidgets } from '../SeriesMissions/SeriesMissionsWidgets/index.tsx';
import { InviteButton } from '../inviteFriends/InviteButton/index.tsx';
import { KycVerificationBanner } from './KycVerificationBanner/index.tsx';
import { NextTrckTile } from './NextTrckTile/index.tsx';
import { SeriesBoardHeader } from './SeriesBoardHeader/index.tsx';
import { SetOnboardingNotificationsPrompt } from './SetOnboardingNotificationsPrompt/index.tsx';
import type {
  SeriesBoard_cardSetRoot,
  SeriesBoard_currentUser,
  SeriesBoard_deliverableItemOrder,
} from './__generated__/index.graphql.ts';
import { useMaybeClaimInviteTask } from './useMaybeClaimInviteTask.ts';

const AbsoluteHeader = styled(Vertical)`
  position: absolute;
  width: 100%;
  top: 0;
  pointer-events: none;
  & > * {
    pointer-events: auto;
  }
  @media ${desktopAndAbove} {
    padding-right: var(--triple-unit);
  }
`;

const BadgesSection = styled(Horizontal)`
  padding: 0 var(--double-unit);
  align-items: flex-start;
  justify-content: flex-end;
  pointer-events: none;

  & > * {
    pointer-events: auto;
  }

  &.withLeftBadge {
    justify-content: space-between;
  }

  @media ${desktopAndAbove} {
    padding-right: 0;
  }
`;

const leftBadges = css`
  align-items: flex-start;
`;

const footerContent = css`
  display: flex;
  width: 100%;
  justify-content: center;
  padding: 0 var(--double-unit) var(--double-unit);
  pointer-events: none;
  &:empty {
    display: none;
  }
  & > * {
    pointer-events: auto;
  }
`;

const desktopFooter = css`
  position: absolute;
  left: 0;
  bottom: var(--triple-unit);
  z-index: 1;
  display: flex;
  width: 100%;
  justify-content: center;
  padding: 0 var(--double-unit);
  pointer-events: none;
  & > * {
    pointer-events: auto;
  }
`;

const MobileDecoration = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  background:
    linear-gradient(
      to bottom,
      var(--c-nl-500) 0%,
      transparent 300px,
      transparent
    ),
    var(--decoration) no-repeat center center / cover;

  @media ${laptopAndAbove} {
    display: none;
  }
`;

type Props = {
  currentUser: SeriesBoard_currentUser;
  cardSet: SeriesBoard_cardSetRoot;
  deliverableItemOrder?: SeriesBoard_deliverableItemOrder;
  refetch: RefetchFunction<unknown, OperationVariables>;
  showConnectTaskDialog: boolean;
  onConnectTaskDialogClose: () => void;
  onConnectTaskDialogOpen: () => void;
  isReplay: boolean;
};

export const SeriesBoard = ({
  currentUser,
  cardSet,
  deliverableItemOrder,
  refetch,
  showConnectTaskDialog,
  onConnectTaskDialogClose,
  onConnectTaskDialogOpen,
  isReplay,
}: Props) => {
  const { isAndroidApp } = useIsMobileApp();
  const isLaptopAndAbove = useIsLaptop();
  const showProOnboardingBadge = useShowProOnboardingBadge();
  const { generateSportContextPath } = useSportContext();
  const commonGameSet = useCommonGameSet();
  const { surfacePrimaryColor } = useCardSetBranding();
  const setNameColor = (surfacePrimaryColor ??
    'var(--c-set-static-surface-primary)') as HexColor;
  const {
    flags: { enablePublicSquads = false },
  } = useFeatureFlags();
  const showSquadEntryBadge = useIsSquadEnabled() && enablePublicSquads;
  const brandingCardSet = cardSet?.currentSet ?? cardSet?.nextSet;
  const boardDecoration = getBoardMobileDecoration(brandingCardSet?.slug);
  const { lifecycle, update: updateLifecycle } = useLifecycle();
  const maybeClaimInviteTask = useMaybeClaimInviteTask();
  const { setTasksTrack, setOnboardingTasks, setGuessTheScore } = currentUser;
  const [openHotStreaksTutorialStory, setOpenHotStreaksTutorialStory] =
    useState(false);
  const { steps } = setTasksTrack || {};
  const thresholdTasks = steps?.filter(isTypeFn('ThresholdPickerTask')) || [];
  const [claimedTaskId, setClaimedTaskId] = useState<string | undefined>();

  const [showTargetValue, setShowTargetValue] = useState(false);

  const claimedTask = thresholdTasks.find(task => task.id === claimedTaskId);

  const currentTask = thresholdTasks.find(
    task => task.aasmState !== ManagerProgressionTaskState.CLAIMED
  );

  const hasSubmittedAnyHotStreakLineup = thresholdTasks.some(
    task => task.taskLineups.length > 0
  );

  useSessionReplayShutdown(hasSubmittedAnyHotStreakLineup);
  const onboardingTaskCompleted = setOnboardingTasks?.find(
    task => task.aasmState === ManagerProgressionTaskState.COMPLETED
  );
  const [completedOnboardingTask, setCompletedOnboardingTask] = useState<
    typeof onboardingTaskCompleted | null
  >(null);
  if (completedOnboardingTask === null && onboardingTaskCompleted) {
    setCompletedOnboardingTask(onboardingTaskCompleted);
  }

  const [refereeTaskToClaim, setRefereeTaskToClaim] = useState(
    currentUser.inviteTaskAsReferee?.aasmState ===
      ManagerProgressionTaskState.COMPLETED
  );
  const referrerInviteTaskToClaim = currentUser.inviteTasksAsReferrer
    ?.filter(isTypeFn('InviteTaskAsReferrer'))
    .find(task =>
      task.steps?.some(
        step => step.aasmState === ManagerProgressionTaskState.COMPLETED
      )
    );
  const [referrerTaskToClaim, setReferrerTaskToClaim] = useState<
    typeof referrerInviteTaskToClaim | null
  >(null);
  if (referrerTaskToClaim === null && referrerInviteTaskToClaim) {
    setReferrerTaskToClaim(referrerInviteTaskToClaim);
  }
  const showReferrerReward =
    !isReplay &&
    !claimedTask &&
    !completedOnboardingTask &&
    !!referrerTaskToClaim;

  const showRefereeReward =
    !isReplay &&
    !claimedTask &&
    !completedOnboardingTask &&
    !showReferrerReward &&
    !!refereeTaskToClaim;

  const showNotificationsPrompt =
    !isReplay &&
    isAndroidApp &&
    !lifecycle?.[LIFECYCLE.sawPushNotificationsPromptInWebview] &&
    hasSubmittedAnyHotStreakLineup;

  const boardBranding = brandingCardSet?.branding;
  const boardBackground = isLaptopAndAbove
    ? boardBranding?.playBoardDesktop
    : boardBranding?.playBoardMobile;

  return (
    <FullHeightBoard
      background={`url(${boardBackground ?? ''})`}
      items={
        isReplay
          ? [
              ...thresholdTasks,
              {
                id: 'replay',
                aasmState: ManagerProgressionTaskState.READY,
              },
            ]
          : thresholdTasks
      }
      renderItem={({ item, index }) => {
        const taskId = idFromObject(item.id);
        const lineupsPath = generateSportContextPath(
          ANY_SPORT_SERIES_TASK_LINEUPS,
          {
            params: { task: taskId },
          }
        );

        const composePath = generateSportContextPath(
          ANY_SPORT_SERIES_TASK_COMPOSE_TEAM,
          {
            params: { task: taskId },
          }
        );
        if (!('__typename' in item)) {
          return (
            <NextTrckTile
              taskId={setTasksTrack?.id || ''}
              cardSet={brandingCardSet}
              onTrckClaimed={async () => {
                await refetch();
              }}
              disabled={thresholdTasks.some(
                task => task.aasmState !== ManagerProgressionTaskState.CLAIMED
              )}
            />
          );
        }
        return (
          <HotStreakTile
            task={item}
            showOnboarding={index === 0}
            onClaimTask={setClaimedTaskId}
            onOpenHotStreaksTutorialStory={() => {
              setOpenHotStreaksTutorialStory(true);
            }}
            showTargetValue={showTargetValue}
            setShowTargetValue={setShowTargetValue}
            lineupsPath={lineupsPath}
            composePath={composePath}
            isReplay={isReplay}
          />
        );
      }}
    >
      {!!(isReplay && boardDecoration) && (
        <MobileDecoration
          style={
            {
              '--decoration': `url(${boardDecoration})`,
            } as CSSProperties
          }
        />
      )}
      <AbsoluteHeader>
        <SeriesBoardHeader
          title={
            <Horizontal>
              <ArcadeModeIcon />
              <Vertical gap={0.5}>
                <BrandS>
                  <FormattedMessage {...seriesModeMessages.arcade} />
                </BrandS>
                <Horizontal gap={0.5}>
                  <BrandXXS color={setNameColor}>{commonGameSet.name}</BrandXXS>
                  {!!commonGameSet.endDate && (
                    <LinkOther as="div">
                      <Horizontal gap={0.5}>
                        <Dot size={0.5} color="var(--c-nd-600)" />
                        <SeriesBoardTimer endDate={commonGameSet.endDate} />
                      </Horizontal>
                    </LinkOther>
                  )}
                </Horizontal>
              </Vertical>
            </Horizontal>
          }
          rightSlot={
            currentTask ? (
              <LifesContainer>
                <Lifes task={currentTask} size={1.5} />
              </LifesContainer>
            ) : undefined
          }
        >
          {!isReplay && !isLaptopAndAbove && <InviteButton />}
        </SeriesBoardHeader>
        {!!isLaptopAndAbove && !isReplay && (
          <Portal id="page-header-right-button">
            <InviteButton />
          </Portal>
        )}
        {!isReplay && (
          <BadgesSection
            className={classNames({
              withLeftBadge:
                !!setGuessTheScore ||
                showProOnboardingBadge ||
                showSquadEntryBadge,
            })}
          >
            {(!!setGuessTheScore ||
              !!showProOnboardingBadge ||
              !!showSquadEntryBadge) && (
              <Vertical gap={1} className={leftBadges}>
                {!!showSquadEntryBadge && (
                  <SquadEntryBadge squad={currentUser.squad} />
                )}
                {(!!setGuessTheScore || !!showProOnboardingBadge) && (
                  <Horizontal gap={1}>
                    {!!setGuessTheScore && (
                      <GuessTheScoreBadge task={setGuessTheScore} />
                    )}
                    {!!showProOnboardingBadge && (
                      <ProOnboardingBadge cardSet={brandingCardSet} />
                    )}
                  </Horizontal>
                )}
              </Vertical>
            )}
            <SeriesMissionsWidgets
              currentUser={currentUser}
              cardSet={cardSet}
              deliverableItemOrder={deliverableItemOrder}
              showConnectTaskDialog={showConnectTaskDialog}
              onConnectTaskDialogClose={onConnectTaskDialogClose}
              onConnectTaskDialogOpen={onConnectTaskDialogOpen}
            />
          </BadgesSection>
        )}
      </AbsoluteHeader>
      {isLaptopAndAbove ? (
        <div className={desktopFooter}>
          <KycVerificationBanner tasks={thresholdTasks} />
        </div>
      ) : (
        <Portal id="above-bottom-bar-portal">
          <div className={footerContent}>
            <KycVerificationBanner tasks={thresholdTasks} />
          </div>
        </Portal>
      )}
      {!!openHotStreaksTutorialStory && (
        <HotStreaksTutorialStory
          open={openHotStreaksTutorialStory}
          onClose={() => {
            setOpenHotStreaksTutorialStory(false);
          }}
          taskTrack={setTasksTrack}
        />
      )}
      <SetOnboardingNotificationsPrompt
        open={showNotificationsPrompt}
        cardSet={brandingCardSet}
        onClose={() => {
          updateLifecycle(LIFECYCLE.sawPushNotificationsPromptInWebview, true);
        }}
      />
      {!!claimedTask && (
        <AnyTaskBasicClaimStory
          onClose={() => {
            refetch();
            setClaimedTaskId(undefined);
          }}
          task={claimedTask}
          showContinueButton
        />
      )}
      {!claimedTask && !!completedOnboardingTask && (
        <AnyTaskBasicClaimStory
          onClose={() => {
            refetch();
            setCompletedOnboardingTask(undefined);
            maybeClaimInviteTask(currentUser);
          }}
          task={completedOnboardingTask}
          showContinueButton
          appendedSteps={
            currentUser.inviteTaskAsReferee
              ? [
                  {
                    bodyElement: ({ onNext }) => (
                      <Fragment key="referee-reward-story">
                        <RefereeExclusiveReward
                          currentUser={currentUser}
                          cardSet={brandingCardSet}
                          onClose={() => {
                            setRefereeTaskToClaim(false);
                            onNext();
                          }}
                        />
                      </Fragment>
                    ),
                  },
                ]
              : undefined
          }
        />
      )}
      {!!showReferrerReward && (
        <StepperDialog
          onClose={() => {
            refetch();
            setReferrerTaskToClaim(undefined);
          }}
          steps={[
            {
              bodyElement: ({ onNext }) => (
                <Fragment key="referrer-reward-story">
                  <ReferrerReward
                    task={referrerTaskToClaim}
                    cardSet={brandingCardSet}
                    onClose={onNext}
                  />
                </Fragment>
              ),
            },
          ]}
        />
      )}
      {!!showRefereeReward && (
        <StepperDialog
          onClose={() => {
            refetch();
            setRefereeTaskToClaim(false);
          }}
          steps={[
            {
              bodyElement: ({ onNext }) => (
                <Fragment key="referee-reward-story">
                  <RefereeExclusiveReward
                    currentUser={currentUser}
                    cardSet={brandingCardSet}
                    onClose={onNext}
                  />
                </Fragment>
              ),
            },
          ]}
        />
      )}
    </FullHeightBoard>
  );
};

SeriesBoard.fragments = {
  currentUser: gql`
    fragment SeriesBoard_currentUser on CurrentUser {
      slug
      squad(sport: $sport) {
        id
        ...SquadEntryBadge_squad
      }
      inviteTaskAsReferee(sport: $sport) {
        id
        aasmState
      }
      inviteTasksAsReferrer(sport: $sport) {
        id
        ...ReferrerReward_inviteTaskAsReferrer
      }
      # eslint-disable-next-line @graphql-eslint/no-deprecated
      setOnboardingTasks(sport: $sport) {
        id
        aasmState
        ...AnyTaskBasicClaimStory_taskInterface
      }
      setGuessTheScore(sport: $sport) {
        id
        ...GuessTheScoreBadge_taskInterface
      }
      setTasksTrack(sport: $sport) {
        id
        steps {
          id
          aasmState
          ... on ThresholdPickerTask {
            id
            averageMode
          }
          ...Lifes_thresholdPickerTask
          ...HotStreakTile_thresholdPickerTask
          ...KycVerificationBanner_thresholdPickerTask
          ...AnyTaskBasicClaimStory_taskInterface
        }
        ...HotStreaksTutorialStory_tasksTrack
      }
      ...SeriesMissionsWidgets_currentUser
      ...RefereeExclusiveReward_currentUser
      ...useMaybeClaimInviteTask_currentUser
    }
    ${Lifes.fragments.thresholdPickerTask}
    ${HotStreakTile.fragments.thresholdPickerTask}
    ${KycVerificationBanner.fragments.thresholdPickerTask}
    ${AnyTaskBasicClaimStory.fragments.taskInterface}
    ${HotStreaksTutorialStory.fragments.tasksTrack}
    ${SeriesMissionsWidgets.fragments.currentUser}
    ${SquadEntryBadge.fragments.squad}
    ${RefereeExclusiveReward.fragments.currentUser}
    ${ReferrerReward.fragments.inviteTaskAsReferrer}
    ${useMaybeClaimInviteTask.fragments.currentUser}
    ${GuessTheScoreBadge.fragments.taskInterface}
  ` as TypedDocumentNode<SeriesBoard_currentUser>,
  cardSet: gql`
    fragment SeriesBoard_cardSetRoot on CardSetRoot {
      id
      currentSet(sport: $sport) {
        slug
        branding {
          id
          playBoardDesktop: assetUrl(slot: PLAY_BOARD_DESKTOP)
          playBoardMobile: assetUrl(slot: PLAY_BOARD_MOBILE)
        }
        ...ReferrerReward_cardSet
        ...RefereeExclusiveReward_cardSet
        ...ProOnboardingBadge_cardSet
        ...SetOnboardingNotificationsPrompt_cardSet
      }
      nextSet(sport: $sport) {
        slug
        branding {
          id
          playBoardDesktop: assetUrl(slot: PLAY_BOARD_DESKTOP)
          playBoardMobile: assetUrl(slot: PLAY_BOARD_MOBILE)
        }
        ...ReferrerReward_cardSet
        ...RefereeExclusiveReward_cardSet
        ...ProOnboardingBadge_cardSet
        ...SetOnboardingNotificationsPrompt_cardSet
      }
      ...SeriesMissionsWidgets_cardSetRoot
    }
    ${SeriesMissionsWidgets.fragments.cardSet}
    ${ReferrerReward.fragments.cardSet}
    ${RefereeExclusiveReward.fragments.cardSet}
    ${ProOnboardingBadge.fragments.cardSet}
    ${SetOnboardingNotificationsPrompt.fragments.cardSet}
  ` as TypedDocumentNode<SeriesBoard_cardSetRoot>,
  deliverableItemOrder: gql`
    fragment SeriesBoard_deliverableItemOrder on DeliverableItemOrder {
      id
      ...SeriesMissionsWidgets_deliverableItemOrder
    }
    ${SeriesMissionsWidgets.fragments.deliverableItemOrder}
  ` as TypedDocumentNode<SeriesBoard_deliverableItemOrder>,
};
